import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "frame", "frameWrapper", "fitButton", "background", "avatarContainer",
    "badgeInput", "headlineInput", "subheadInput", "snippetToggle",
    "badgeText", "headlineText", "subheadText", "snippetBlock"
  ]

  disconnect() {
    if (this._resizeHandler) window.removeEventListener("resize", this._resizeHandler)
  }

  toggleFit() {
    this._fitActive = !this._fitActive
    if (this._fitActive) {
      this._applyFit()
      this._resizeHandler = () => this._applyFit()
      window.addEventListener("resize", this._resizeHandler)
      if (this.hasFitButtonTarget) this.fitButtonTarget.textContent = "Actual size"
    } else {
      this._clearFit()
      if (this._resizeHandler) {
        window.removeEventListener("resize", this._resizeHandler)
        this._resizeHandler = null
      }
      if (this.hasFitButtonTarget) this.fitButtonTarget.textContent = "Fit to screen"
    }
  }

  _applyFit() {
    if (!this.hasFrameTarget || !this.hasFrameWrapperTarget) return
    const width = parseInt(this.frameTarget.dataset.width)
    const height = parseInt(this.frameTarget.dataset.height)
    const available = this.frameWrapperTarget.clientWidth
    const scale = Math.min(1, available / width)
    this.frameTarget.style.transform = `scale(${scale})`
    this.frameWrapperTarget.style.height = `${height * scale}px`
    this.frameWrapperTarget.style.overflow = "hidden"
  }

  _clearFit() {
    if (this.hasFrameTarget) this.frameTarget.style.transform = ""
    if (this.hasFrameWrapperTarget) {
      this.frameWrapperTarget.style.height = ""
      this.frameWrapperTarget.style.overflow = ""
    }
  }

  updateText() {
    if (this.hasBadgeTextTarget && this.hasBadgeInputTarget) {
      this.badgeTextTarget.textContent = this.badgeInputTarget.value
    }
    if (this.hasHeadlineTextTarget && this.hasHeadlineInputTarget) {
      const raw = this.headlineInputTarget.value
      this.headlineTextTarget.innerHTML = raw
        .replace(/\n/g, '<br>')
        .replace(/\*([^*]+)\*/g, '<span style="color: #0d7d6e;">$1</span>')
    }
    if (this.hasSubheadTextTarget && this.hasSubheadInputTarget) {
      this.subheadTextTarget.textContent = this.subheadInputTarget.value
    }
    this.toggleSnippet()
  }

  toggleSnippet() {
    if (!this.hasSnippetBlockTarget) return
    const show = this.hasSnippetToggleTarget ? this.snippetToggleTarget.checked : true
    this.snippetBlockTargets.forEach(el => { el.style.display = show ? '' : 'none' })
  }

  async download() {
    const frame = this.frameTarget
    const width = parseInt(frame.dataset.width)
    const height = parseInt(frame.dataset.height)

    // Open a clean popup at exact pixel dimensions
    const popup = window.open('', '_blank', `width=${width + 20},height=${height + 20},scrollbars=no,menubar=no,toolbar=no`)
    if (!popup) {
      alert('Please allow popups to open the image for saving.')
      return
    }

    const doc = popup.document
    doc.open()
    doc.write('<!DOCTYPE html><html><head>')
    doc.write(`<title>primeta — ${width}x${height}</title>`)

    // Copy stylesheets
    for (const link of document.querySelectorAll('link[rel="stylesheet"]')) {
      doc.write(`<link rel="stylesheet" href="${link.href}">`)
    }
    for (const style of document.querySelectorAll('style')) {
      doc.write(`<style>${style.textContent}</style>`)
    }

    // CSS variables
    const rootStyle = getComputedStyle(document.documentElement)
    const vars = ['--color-bg', '--color-bg-alt', '--color-fg', '--color-fg-muted',
                   '--color-accent', '--color-accent-dk', '--color-border', '--color-border-lt',
                   '--font-mono', '--font-sans']
    let rootCSS = ':root {'
    for (const v of vars) rootCSS += `${v}: ${rootStyle.getPropertyValue(v)};`
    rootCSS += '}'
    doc.write(`<style>${rootCSS} * { margin: 0; padding: 0; } body { margin: 0; overflow: hidden; background: #000; display: flex; align-items: start; justify-content: start; }</style>`)

    // Google Fonts
    for (const link of document.querySelectorAll('link[href*="fonts.googleapis"]')) {
      doc.write(`<link rel="stylesheet" href="${link.href}">`)
    }

    doc.write('</head><body>')

    // Clone frame at exact size
    const clone = frame.cloneNode(true)
    clone.style.transform = 'none'
    clone.style.width = `${width}px`
    clone.style.height = `${height}px`
    clone.style.minWidth = `${width}px`
    clone.style.minHeight = `${height}px`
    clone.style.position = 'relative'
    clone.style.border = 'none'
    // Remove WebGL canvases (will be replaced with static images)
    clone.querySelectorAll('canvas').forEach(c => c.remove())
    doc.write(clone.outerHTML)
    doc.write('</body></html>')
    doc.close()

    // Capture WebGL canvases as static images and insert into popup
    const frameRect = frame.getBoundingClientRect()
    const avatarDivs = frame.querySelectorAll("[data-controller='avatar']")
    const popupFrame = popup.document.body.firstElementChild

    // Wait for fonts to load
    await new Promise(r => setTimeout(r, 800))

    for (const div of avatarDivs) {
      const webglCanvas = div.querySelector("canvas")
      if (!webglCanvas) continue
      const ctrl = div.avatarController
      if (ctrl?.renderer && ctrl?.scene && ctrl?.camera) {
        ctrl.renderer.render(ctrl.scene, ctrl.camera)
      }

      // Find matching container in the popup clone
      const slug = div.dataset.avatarPersonaValue
      const popupDiv = popupFrame.querySelector(`[data-avatar-persona-value="${slug}"]`)
      if (!popupDiv) continue

      const img = popup.document.createElement('img')
      img.src = webglCanvas.toDataURL('image/png')
      img.style.cssText = 'width:100%;height:100%;object-fit:contain;position:absolute;top:0;left:0;'
      popupDiv.style.position = 'relative'
      popupDiv.appendChild(img)
    }

    // Add instructions
    const hint = popup.document.createElement('div')
    hint.style.cssText = 'position:fixed;bottom:0;left:0;right:0;background:rgba(0,0,0,0.85);color:#fff;text-align:center;padding:12px;font:13px system-ui;z-index:9999;'
    hint.textContent = 'Right-click the image → "Save Image As..." or use ⌘+Shift+4 (Mac) / Win+Shift+S (Windows) to screenshot'
    popup.document.body.appendChild(hint)
  }
}
